/**
 * Native Interactive Site Map (ISM) Modal Handler
 *
 * Pure coordinator: registers the modal with the modal service and manages
 * its open/close event lifecycle. Receives all dependencies as arguments; owns
 * no global state and produces no side effects beyond what is passed in.
 *
 * @param {Object} params
 * @param {Element}  params.appRoot       - The live ISM root element (outerHTML is used as modal payload).
 * @param {Object}   params.modalService  - Modal service instance (setHtml, onHtmlReady, onHtmlRemoved, clearHtml).
 * @param {Function} params.dispatch      - Redux store dispatch.
 * @param {Function} params.modalAction   - Action registering modal (e.g. uiStateSlice.actions.modalInformation).
 * @param {Function} params.onReady       - Callback invoked with the live modal root when markup is ready.
 *   Must run, and complete, before `onBootstrap` — see the ordering note below.
 * @param {Function} params.onBootstrap   - Async callback invoked with the live modal root once the modal is
 *   ready, after `onReady` has run.
 * @param {Function} params.onStop        - Callback invoked when the modal close button is clicked; stops plugin host.
 *
 * Ordering contract: `onReady` MUST run to completion before `onBootstrap` starts (see the call
 * site below). In this feature, `onReady` is `drawerLifecycle.onDrawerReady`, which dispatches
 * this drawer's identity (`drawerInformation({ name })`) to the Redux store, and `onBootstrap`
 * is `bootstrapNativeInteractiveSiteMap`, which eventually calls `drawerService.mountElement(...)`
 * via `drawer.plugin.js`. `drawer.js`'s `mountElement`/`openDrawer` handlers read
 * `selectDrawer(store.getState())?.name` synchronously to know which registered callbacks belong
 * to this drawer — if `onBootstrap` ran first, that read would see stale/missing drawer identity
 * and the mount could register under the wrong name (or none).
 */
export default function createInteractiveSiteMapModalHandler({
  appRoot,
  modalService,
  dispatch,
  modalAction,
  onReady,
  onBootstrap,
  onStop,
}) {
  const MODAL_ID = 'native-interactive-site-map-modal';
  const modalElement = document.getElementById(MODAL_ID);

  const onCloseButtonClick = async (event) => {
    if (!event.target.closest('[data-close-button]')) {
      return;
    }
    modalService.clearHtml();
    await onStop();
  };

  const bindEvents = () => {    
    if (!modalElement) {
      return;
    }
    modalElement.addEventListener('click', onCloseButtonClick);
  };

  const unbindEvents = () => {
    if (!modalElement || !onCloseButtonClick) {
      return;
    }
    modalElement.removeEventListener('click', onCloseButtonClick);
  };

  dispatch(modalAction({ name: MODAL_ID }));

  modalService.onHtmlReady({
    name: MODAL_ID,
    fn: async () => {
      const modalRoot = modalElement?.querySelector('[data-native-interactive-site-map]');
      if (!modalRoot) {
        return;
      }

      // onReady before onBootstrap is a hard ordering requirement, not incidental — see the
      // "Ordering contract" note in this file's header JSDoc. Awaited so the guarantee holds
      // even if onReady is ever made async.
      if (typeof onReady === 'function') {
        await onReady(modalRoot);
      }

      bindEvents();
      await onBootstrap(modalRoot);
    },
  });

  modalService.onHtmlRemoved({
    name: MODAL_ID,
    fn: unbindEvents,
  });
  
  modalService.setHtml({ payload: appRoot.innerHTML });
}
